import ReferenceUserComponent from './components/ReferenceUserComponent';
import {
  type ReferenceUserObject,
  AhaEditorUtils,
  slate,
  type UserReferenceElement,
  type EditorPlugin,
  MenuPluginFactory,
} from '@aha-app/aha-editor-v2';
import * as AbortableRequest from '../../AbortableRequest';
import getMenuItemComponent from './components/getMenuItemComponent';

// Aha! User Reference Plugin
// This plugin allows users to add Aha! user mentions to their content. It's a
// function that returns two plugins:
// 1. A component plugin for rendering the user reference element in the editor
// 2. A menu plugin that provides search and selection functionality for adding user mentions. This
//    plugin uses the MenuPluginFactory and is triggered by the '@' character.

interface ReferenceUserPluginOptions {
  // If provided, user will become a watcher of the document after being added as a mention
  watchURL?: string;
  // Callback to fire after the user is added as a watcher
  watchCallbackSelector?: string;
  // Used to filter search results to a project
  projectId?: RecordId;
  // Search URL for users. A different URL is used in Ideas portals
  searchURL?: string;
  // Whether to show the user's avatar in the selection menu
  showAvatar?: boolean;
}

const ReferenceUserPlugin = ({
  watchURL,
  watchCallbackSelector,
  projectId,
  searchURL,
  showAvatar,
}: ReferenceUserPluginOptions): EditorPlugin[] => {
  const requestCache = AbortableRequest.createRequestCache();

  const addWatchers = ({
    watchURL,
    watchCallbackSelector,
    reference,
  }: {
    watchURL?: string;
    watchCallbackSelector?: string;
    reference: ReferenceUserObject;
  }) => {
    if (!watchURL) return;

    if (watchCallbackSelector) {
      watchURL = watchURL.includes('?') ? `${watchURL}&` : `${watchURL}?`;
      watchURL = `${watchURL}callback=${encodeURIComponent(
        watchCallbackSelector
      )}`;
    }

    void $.ajax({
      url: watchURL,
      type: 'POST',
      data: {
        watch: {
          user_id: reference.id,
          user_type: reference.class,
        },
      },
      dataType: 'script',
    });
  };

  const MenuItemComponent = getMenuItemComponent(showAvatar);

  const ReferenceUserMenuPlugin: EditorPlugin =
    MenuPluginFactory<ReferenceUserObject>({
      name: 'user-reference',
      triggerCharacter: '@',
      menuItemUniqueKey: 'id',
      onSearch: async ({ text }) => {
        function pick(text: string) {
          const projectParam = projectId ? `&project_id=${projectId}` : '';
          const url = `${
            searchURL || '/search/users'
          }?q=${text}${projectParam}`;
          return AbortableRequest.create(url, response => response.users);
        }

        requestCache.abort();
        text = text.toLowerCase().trim();
        return requestCache.add(text, () => pick(text));
      },
      MenuItemComponent,
      onSelect: ({ item: reference, editor, controller }) => {
        AhaEditorUtils.Marks.withRestoredMarks(editor, editor => {
          // Insert the user reference at the current selection
          let node: UserReferenceElement;
          if (reference.class === 'Ideas::PortalUser') {
            node = {
              type: 'user-reference',
              reference_class: 'Ideas::PortalUser',
              reference_id: reference.id,
              reference_name: reference.name,
              children: [{ text: reference.name }],
            };
          } else {
            node = {
              type: 'user-reference',
              reference_class: reference.class,
              reference_name: reference.name,
              reference_url: reference.url,
              children: [{ text: reference.name }],
            };
          }
          slate.Transforms.insertNodes(editor, node);

          // Move cursor after the inserted node
          AhaEditorUtils.Selection.moveAfterCurrentNode(editor);
        });

        slate.Transforms.insertText(editor, ' ');

        addWatchers({
          watchURL,
          watchCallbackSelector,
          reference,
        });
      },
      minCharacters: 0,
    });

  const ReferenceUserComponentPlugin: EditorPlugin = {
    name: 'Reference user component plugin',
    components: [
      {
        type: 'user-reference',
        component: ReferenceUserComponent,
      },
    ],
  };

  return [ReferenceUserComponentPlugin, ReferenceUserMenuPlugin];
};

export default ReferenceUserPlugin;
